import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import "./App.css";

const API_BASE = "/api";

// Tell axios to always send cookies with every request
axios.defaults.withCredentials = true;

function App() {
  const [step, setStep] = useState("login");
  const [form, setForm] = useState({ username: "", password: "", mfa: "" });
  const [stayLoggedIn, setStayLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [message, setMessage] = useState({ text: "", type: "" });

  const showMessage = useCallback((text, type = "info") => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: "", type: "" }), 6000);
  }, []);

  const clearAuth = useCallback((showExpiredMsg = false) => {
    setUser(null);
    setStep("login");
    setForm({ username: "", password: "", mfa: "" });
    if (showExpiredMsg) {
      showMessage("Your session has expired or is invalid. Please log in again.", "info");
    }
  }, [showMessage]);

  // Check authentication via cookie — no token needed, browser sends cookie automatically
  const checkAuth = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/me`);
      if (res.data.user) {
        setUser(res.data.user);
        setStep("dashboard");
        showMessage("Welcome back!", "success");
      } else {
        // /me returned 200 but no user — session exists but is invalid
        clearAuth(true);
      }
    } catch (err) {
      // 401/403 = no session cookie at all, just go to login silently
      setStep("login");
    }
  }, [clearAuth, showMessage]);

  // On mount, check if the browser already has a valid session cookie
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const login = async (e) => {
    e.preventDefault();
    try {
      const formData = new URLSearchParams();
      formData.append("username", form.username);
      formData.append("password", form.password);
      formData.append("stayLoggedIn", stayLoggedIn);

      const res = await axios.post(
        `${API_BASE}/login`,
        formData,
        { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
      );

      if (res.data.mfa) {
        setStep("mfa");
        showMessage("MFA required. Please enter your code.", "info");
      } else if (res.data.user) {
        // Cookie is set by the server in the response — no need to store anything
        setUser(res.data.user);
        setStep("dashboard");
        showMessage("Login successful!", "success");
      }
    } catch (err) {
      console.error("Login error:", err);
      showMessage("Login failed. Check username/password.", "error");
    }
  };

  const submitMFA = async (e) => {
    e.preventDefault();
    try {
      const formData = new URLSearchParams();
      formData.append("username", form.username);
      formData.append("code", form.mfa);

      const res = await axios.post(
        `${API_BASE}/mfa`,
        formData,
        { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
      );

      if (res.data.user) {
        // Cookie is set by the server in the response
        setUser(res.data.user);
        setStep("dashboard");
        showMessage("MFA verified. Welcome!", "success");
      }
    } catch (err) {
      console.error("MFA error:", err);
      showMessage("Invalid MFA code.", "error");
    }
  };

  const disableMFA = async () => {
    try {
      await axios.post(
        `${API_BASE}/disable-mfa`,
        null,
        { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
      );
      showMessage("MFA disabled successfully.", "success");
    } catch (err) {
      console.error("Disable MFA error:", err);
      showMessage("Failed to disable MFA.", "error");
    }
  };

  const enableMFA = async () => {
    try {
      await axios.post(
        `${API_BASE}/enable-mfa`,
        null,
        { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
      );
      showMessage("MFA enabled successfully.", "success");
    } catch (err) {
      console.error("Enable MFA error:", err);
      showMessage("Failed to enable MFA.", "error");
    }
  };

  const logout = async () => {
    try {
      await axios.post(
        `${API_BASE}/logout`,
        null,
        { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
      );
    } catch (err) {
      console.error("Logout error:", err);
    }
    clearAuth(false);
    showMessage("You have been logged out.", "info");
  };

  return (
    <div className="akira-bg">
      <div className="logo-container">
        <img
          src="https://cdn.prod.website-files.com/69b3cac005124285ea612325/69b794936957731b3595ac5b_Redfox%20Cybersecurity%20logo%20v2.svg"
          alt="redfox Logo"
          className="akira-logo"
        />
      </div>

      {message.text && (
        <div className={`message-box ${message.type}`}
          style={{ color: message.color || "White", fontWeight: "bold" }}>
          {message.text}
        </div>
      )}

      {step === "login" && (
        <form className="auth-box" onSubmit={login}>
          <h2>RedFox</h2>
          <input
            name="username"
            placeholder="Username"
            value={form.username}
            onChange={handleChange}
            required
            autoFocus
          />
          <input
            type="password"
            name="password"
            placeholder="Password"
            value={form.password}
            onChange={handleChange}
            required
          />
          <label className="stay-logged-in-label">
            <input
              type="checkbox"
              checked={stayLoggedIn}
              onChange={() => setStayLoggedIn((prev) => !prev)}
            />{" "}
            Stay Logged In
          </label>
          <button type="submit"> Login </button>
        </form>
      )}

      {step === "mfa" && (
        <form className="auth-box" onSubmit={submitMFA}>
          <h2>Enter your MFA code</h2>
          <input
            name="mfa"
            placeholder="MFA Code"
            value={form.mfa}
            onChange={handleChange}
            required
            autoFocus
          />
          <button type="submit">SUBMIT</button>
        </form>
      )}

      {step === "dashboard" && user && (
        <div className="auth-box dashboard-box">
          <h2>Welcome back!</h2>
          <div className="user-info">
            <div>👤 {user.username}</div>
            <div>✉️ {user.email}</div>
            <div>🆔 {user.id}</div>
          </div>
          <button className="danger" onClick={disableMFA}>DISABLE MFA</button>
          <button className="danger" onClick={enableMFA}>ENABLE MFA</button>
          <button onClick={logout}>LOGOUT</button>
        </div>
      )}
    </div>
  );
}

export default App;